import React, { useEffect, useMemo, useState } from 'react';
// components
import Info from './components/Info';
import {
  Box,
  ToggleButtonGroup,
  ToggleButton,
  MenuItem,
  Select,
  ThemeProvider,
  IconButton,
  CircularProgress,
} from '@mui/material';
import { Container, Section, Infos } from './styles';
import { Typography } from '@mui/material';
import Tab from '@/components/UI/mobile/Tab';
import {
  AiOutlineCloudDownload,
  AiFillWarning,
} from 'react-icons/ai';
import DatePicker from '@/components/UI/mobile/DatePicker';
import DownloadHistory from './components/DownloadHistory';
import Chart from './components/Chart';
// data fetch
import useService from '@/lib/hooks/useService';
import getMqttClient from '@/lib/mqtt';
import { useSelector } from 'react-redux';
import { RootState } from '@/redux/store';
import axios from 'axios';
// utils
import client from '@/lib/feathers';
import moment from 'moment';
import { disabledTimes, getExpenseRate, getUsageData } from './utils';
import muiTheme from '@/styles/Theme/mui';
// types
import type { ChargingPile } from '@/types/database/chargingPile/chargingPile';
import type { ChargingPileElectricityRate } from '@/types/database/chargingPile/chargingPileElectricityRate';
import type { MqttPileData } from '@/types/database/chargingPile/chargingPileMqttData';

import { Global, css } from '@emotion/react';

type socketData = {
  name: string;
  text: string;
  group: string;
  type: string;
  value: string;
};

const pileInitialStatus: MqttPileData = {
  voltage: undefined,
  current: undefined,
  power: undefined,
  energy: undefined,
  isCharging: false,
  isVoltage: false,
  status: 'standby',
};

const status = {
  charging: '充電中',
  standby: '待機中',
  noSignal: '無訊號',
  waiting: '排隊順序：',
};

const EnergyFrontDesk: React.FC = () => {
  const [pageStatus, setPageStatus] = useState<'loading' | 'loaded' | 'noData'>(
    'loading'
  );
  const [selectedPile, setSelectedPile] = useState<ChargingPile>();
  const [chartRangeType, setChartRangeType] = React.useState<
    'day' | 'week' | 'month' | 'year' | 'total'
  >('day');
  const [selectedDate, setSelectedDate] = React.useState<moment.Moment>(
    moment()
  );
  const [allowDates, setAllowDates] = React.useState<string[]>([]);
  const [mqttData, setMqttData] = useState<MqttPileData>(pileInitialStatus);
  const [baDeviceStatus, setBaDeviceStatus] = useState<
    '開啟' | '關閉' | '取得資料中'
  >('取得資料中');
  const [usageData, setUsageData] = useState<{
    usage: number;
    cost: number;
    updateTime: string;
  }>({
    usage: 0,
    cost: 0,
    updateTime: moment().format('MM-DD HH:mm'),
  });
  const [showMeter, setShowMeter] = useState<'energy' | 'expense'>('energy');
  const [showContent, setShowContent] = useState<'download' | 'data'>('data');
  const [currentExpenseSetting, setCurrentExpenseSetting] = useState<{
    rateName: string;
    rate: string;
    lastUpdateMinute: moment.Moment;
  }>({
    rateName: '無資料',
    rate: '無資料',
    lastUpdateMinute: moment(),
  });

  const user = useSelector((state: RootState) => state.user.auth.user);

  const houseNumbers = useMemo(() => user?.householdNumbers, [user]);

  const [community] = useService({
    collectionName: 'communityData',
    query: {
      $select: ['englishName'],
    },
  });

  const [piles, , , loading] = useService<ChargingPile>({
    collectionName: 'chargingPile',
    query: {
      householdNumber: {
        $in: houseNumbers,
      },
    },
  });

  const [expenseSetting] = useService<ChargingPileElectricityRate>({
    collectionName: 'chargingPileElectricityRate',
  });

  useEffect(() => {
    if (selectedPile) {
      client.io.on('updatemultistate', (arr: socketData[]) => {
        const status = arr.find(
          (item) => item.text === `電表通電${selectedPile.chargingPileID}`
        );
        if (status) {
          setBaDeviceStatus(status.value as '開啟' | '關閉');
        }
      });
    }
    return () => {
      client.io.off('updatemultistate');
    };
  }, [selectedPile]);

  useEffect(() => {
    const getBaDeviceStatus = async () => {
      if (selectedPile) {
        setBaDeviceStatus('取得資料中');
        const query = {
          text: `電表通電${selectedPile.chargingPileID}`,
        };
        try {
          const res = await client.service('baDevice').find({
            query,
          });
          if (res.data.length > 0) {
            setBaDeviceStatus(res.data[0].value as '開啟' | '關閉');
          }
        } catch (error) {
          console.error(error);
        }
      }
    };
    getBaDeviceStatus();
  }, [selectedPile]);

  useEffect(() => {
    if (
      expenseSetting.length > 0 &&
      moment().isAfter(currentExpenseSetting.lastUpdateMinute)
    ) {
      setCurrentExpenseSetting(getExpenseRate(expenseSetting[0]));
    }
  }, [currentExpenseSetting.lastUpdateMinute, expenseSetting, mqttData]);

  useEffect(() => {
    let statusTimeout: ReturnType<typeof setTimeout>;
    if (loading) {
      setPageStatus('loading');
    } else if (!loading && piles.length === 0) {
      statusTimeout = setTimeout(() => setPageStatus('noData'), 1000);
    } else {
      statusTimeout = setTimeout(() => setPageStatus('loaded'), 1000);
    }
    return () => clearTimeout(statusTimeout);
  }, [piles, loading]);

  useEffect(() => {
    if (piles.length > 0) {
      setSelectedPile(piles[0]);
    }
  }, [piles]);

  useEffect(() => {
    if (selectedPile && community[0]) {
      const mqttClient = getMqttClient();
      mqttClient.subscribe(
        `EZchargingPile/status/${community[0].englishName}/chargingPiles/${selectedPile.chargingPileID}/#`
      );
      mqttClient.on('message', (topic, message) => {
        const key = topic.split('/').pop();
        if (key) {
          setMqttData((prev) => ({
            ...prev,
            [key]: message.toString(),
          }));
        }
      });
      return () => {
        mqttClient.end();
        setMqttData(pileInitialStatus);
        setUsageData({
          usage: 0,
          cost: 0,
          updateTime: moment().format('MM-DD HH:mm'),
        });
      };
    }
    return;
  }, [community, selectedPile]);

  useEffect(() => {
    (async () => {
      if (selectedPile) {
        const newUsageData = await getUsageData(selectedPile);
        if (newUsageData) {
          setUsageData(newUsageData);
        }
      }
    })();
  }, [selectedPile]);

  const getAllowDates = async (id: string) => {
    try {
      const res = await axios.get('/ezcon/api/chargingPileMqttDataDailyDate', {
        headers: {
          authorization: window.localStorage.getItem('feathers-jwt'),
        },
        params: {
          type: 'chargingPile',
          id,
        },
      });
      setAllowDates(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    if (selectedPile) {
      getAllowDates(selectedPile._id);
    }
  }, [selectedPile]);

  return (
    <ThemeProvider theme={muiTheme}>
      <Global
        styles={css`
          html,
          body {
            font-size: 4vw;
            @media (orientation: landscape) {
              font-size: 2vw;
            }
          }
        `}
      />
      {pageStatus !== 'loaded' && (
        <Container>
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
          >
            {pageStatus === 'loading' && (
              <>
                <CircularProgress
                  size="5rem"
                  sx={{
                    marginBottom: '1rem',
                  }}
                />
                <Typography>載入中...</Typography>
              </>
            )}
            {pageStatus === 'noData' && (
              <>
                <Box
                  sx={{
                    fontSize: '5rem',
                  }}
                >
                  <AiFillWarning color="rgb(238,167,33)" />
                </Box>
                <Typography
                  sx={{
                    textAlign: 'center',
                  }}
                >
                  無充電樁資料，若有疑問請與管理中心聯絡
                </Typography>
              </>
            )}
          </Box>
        </Container>
      )}
      {pageStatus === 'loaded' && (
        <>
          {showContent === 'data' && (
            <Container>
              <Section>
                <Info
                  title="車位編號"
                  value={
                    <Select
                      value={selectedPile?._id || ''}
                      disabled={piles.length === 0}
                      onChange={(e) =>
                        setSelectedPile(
                          piles?.find((pile) => pile._id === e.target.value)
                        )
                      }
                    >
                      {piles.map((pile) => (
                        <MenuItem key={pile._id} value={pile._id}>
                          {pile.parkingNumber}
                        </MenuItem>
                      ))}
                    </Select>
                  }
                />
              </Section>
              <Section>
                <Typography variant="h1">充電樁即時狀態</Typography>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(3, 1fr)',
                    gridGap: '1rem',
                    '@media (orientation: landscape)': {
                      display: 'flex',
                      justifyContent: 'space-between',
                    },
                  }}
                >
                  <Info
                    title="電壓"
                    value={
                      mqttData.voltage ? `${mqttData.voltage}伏特` : '無狀態'
                    }
                  />
                  <Info
                    title="電流"
                    value={
                      mqttData.current ? `${mqttData.current}安培` : '無狀態'
                    }
                  />
                  <Info
                    title="功率"
                    value={mqttData.power ? `${mqttData.power}千瓦` : '無狀態'}
                  />
                  <Info
                    title="計費時段"
                    value={currentExpenseSetting.rateName}
                  />
                  <Info
                    title="計費標準"
                    value={`${currentExpenseSetting.rate}元/度`}
                  />
                  <Info
                    title="充電狀態"
                    value={
                      mqttData.status
                        ? `${status[mqttData.status]}${
                            mqttData.status === 'waiting'
                              ? mqttData.waitingOrder || '查詢中'
                              : ''
                          }`
                        : '無狀態'
                    }
                  />
                  {expenseSetting?.[0].manualControl && (
                    <Info title="充電樁開關" value={baDeviceStatus} />
                  )}
                </Box>
              </Section>
              <Section>
                <Typography variant="h1">{`歷史紀錄(${moment()
                  .startOf('M')
                  .format('YYYY-MM-DD HH:mm')}~${
                  usageData.updateTime
                })`}</Typography>
                <Infos>
                  <Info title="月用量" value={`${usageData.usage}度`} />
                  <Info
                    title="本月電費"
                    value={
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'row',
                          alignItems: 'baseline',
                        }}
                      >
                        <Typography
                          variant="body1"
                          sx={{
                            fontWeight: 600,
                            color: '#A85B5B',
                          }}
                        >
                          $
                        </Typography>
                        <Typography
                          variant="body1"
                          sx={{
                            fontSize: '1.4rem',
                            fontWeight: 600,
                            color: '#A85B5B',
                          }}
                        >
                          {usageData.cost}
                        </Typography>
                      </Box>
                    }
                  />
                </Infos>
              </Section>
              <Box
                sx={{
                  '& > *': {
                    marginBottom: '1rem',
                  },
                  '& > *:last-child': {
                    marginBottom: 0,
                  },
                }}
              >
                <Typography variant="h1">歷史資料圖表</Typography>
                <Tab
                  options={['費用', '用電量']}
                  onChange={(val) =>
                    setShowMeter(val === 0 ? 'expense' : 'energy')
                  }
                />
                <Box
                  sx={{
                    '& > *': {
                      marginRight: '1rem',
                    },
                    '& > *:last-child': {
                      marginRight: 0,
                    },
                  }}
                >
                  <ToggleButtonGroup
                    value={chartRangeType}
                    onChange={(_, val: typeof chartRangeType | null) => {
                      if (val) {
                        setChartRangeType(val);
                      }
                    }}
                    exclusive
                  >
                    <ToggleButton key={'day'} value={'day'}>
                      日
                    </ToggleButton>
                    <ToggleButton key={'week'} value={'week'}>
                      週
                    </ToggleButton>
                    <ToggleButton key={'month'} value={'month'}>
                      月
                    </ToggleButton>
                    <ToggleButton key={'year'} value={'year'}>
                      年
                    </ToggleButton>
                    <ToggleButton key={'total'} value={'total'}>
                      全部
                    </ToggleButton>
                  </ToggleButtonGroup>
                  <IconButton onClick={() => setShowContent('download')}>
                    <AiOutlineCloudDownload color="#08c" />
                  </IconButton>
                </Box>
                <Box>
                  <DatePicker
                    value={selectedDate}
                    onChange={(date) => {
                      setSelectedDate(date);
                    }}
                    pickerType={
                      chartRangeType === 'total' ? 'year' : chartRangeType
                    }
                    disabled={chartRangeType === 'total'}
                    disabledTimes={disabledTimes(allowDates)}
                  />
                </Box>
                {/* 圖表 */}
                <Box
                  sx={{
                    height: '24rem',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-around',
                  }}
                >
                  {selectedPile && selectedDate && chartRangeType && (
                    <Chart
                      {...{
                        pile: selectedPile,
                        selectedDate,
                        chartType: chartRangeType,
                        showMeter,
                      }}
                    />
                  )}
                </Box>
              </Box>
            </Container>
          )}
          {showContent === 'download' && (
            <Container>
              <DownloadHistory
                {...{
                  piles: piles,
                  closePage: () => setShowContent('data'),
                }}
              />
            </Container>
          )}
        </>
      )}
    </ThemeProvider>
  );
};

export default EnergyFrontDesk;
